import React, { useEffect } from 'react';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Box,
  Checkbox,
  Chip,
  Container,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import './ManagerCreateTicketView.scss';
import { useNavigate, useSearchParams } from 'react-router-dom';
import UploadTicket, {
  ParsedTicketData,
} from '../../components/UploadTicket/UploadTicket';
import TicketOcrVerifyDialog from '../../components/TicketOcrVerifyDialog/TicketOcrVerifyDialog';
import { applyParsedTicketData } from '../../utils/applyParsedTicketData';
import ProsecutorSelect from '../../components/ProsecutorSelect/ProsecutorSelect';
import CourtSelect from '../../components/CourtSelect/CourtSelect';
import { FullProsecutorDto } from '../../api/ProsecutorApi';
import { FullCourtDto } from '../../api/CourtApi';
import ProductApi from '../../api/ProductApi';
import { ProductDto } from '../../interfaces/ProductInterfaces';
import {
  productMatchesTicketLicenseType,
  ticketLicenseTypeToCode,
} from '../../utils/productLicenseType';
import GenderPronounFields from '../../components/GenderPronounFields/GenderPronounFields';
import { isCourtSelected } from '../../utils/intakeValidation';
import {
  isGenderPronounValid,
  PronounSet,
  TicketGender,
} from '../../utils/ticketGenderPronouns';

const CREATE_TICKET_STEP_LABELS = [
  'Upload Ticket',
  'Enter Details',
  'Review & Submit',
];

const ManagerCreateTicketView = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const userGuid = searchParams.get('User_guid');

  const [activeStep, setActiveStep] = React.useState(0);
  const [uploadCompleted, setUploadCompleted] = React.useState(false);
  const [uploadedDocumentGuid, setUploadedDocumentGuid] = React.useState<
    string | null
  >(null);
  const [uploadedViaImage, setUploadedViaImage] = React.useState(false);
  const [ocrParseSucceeded, setOcrParseSucceeded] = React.useState(true);
  const [ocrVerifyModalOpen, setOcrVerifyModalOpen] = React.useState(false);
  const [ocrVerifyAcknowledged, setOcrVerifyAcknowledged] =
    React.useState(false);

  const [canContinue, setCanContinue] = React.useState(false);

  const [ticketNumber, setTicketNumber] = React.useState('');
  const [ticketCitation, setTicketCitation] = React.useState('');
  const [ticketAddress, setTicketAddress] = React.useState('');
  const [ticketCity, setTicketCity] = React.useState('');
  const [ticketCounty, setTicketCounty] = React.useState('');
  const [ticketCourtDate, setTicketCourtDate] = React.useState('');
  const [ticketRespondByDate, setTicketRespondByDate] = React.useState('');
  const [ticketLicenseType, setTicketLicenseType] = React.useState('');
  const [ticketZone, setTicketZone] = React.useState('');
  const [ticketUrl, setTicketUrl] = React.useState('');
  const [driversLicenseNumber, setDriversLicenseNumber] = React.useState('');
  const [ticketGender, setTicketGender] = React.useState<TicketGender>('');
  const [ticketPronounSet, setTicketPronounSet] = React.useState<PronounSet>('');
  const [hasTakenDscInPastYear, setHasTakenDscInPastYear] =
    React.useState(false);
  const [coverage, setCoverage] = React.useState('');
  const [selectedProsecutor, setSelectedProsecutor] =
    React.useState<FullProsecutorDto | null>(null);
  const [selectedCourt, setSelectedCourt] = React.useState<FullCourtDto | null>(
    null,
  );
  const [products, setProducts] = React.useState<ProductDto[]>([]);
  const [loadingProducts, setLoadingProducts] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  // Fetch products on component mount
  React.useEffect(() => {
    const fetchProducts = async () => {
      setLoadingProducts(true);
      try {
        const result = await ProductApi.getProducts({ from: 0, to: 100 });
        setProducts(result.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoadingProducts(false);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    if (!coverage) return;

    const selectedProduct = products.find(
      (product) => product.Product_Name === coverage,
    );
    if (
      selectedProduct &&
      !productMatchesTicketLicenseType(
        selectedProduct.Product_LicenseType,
        ticketLicenseType,
      )
    ) {
      setCoverage('');
    }
  }, [ticketLicenseType, products, coverage]);

  const matchingProducts = React.useMemo(
    () =>
      products.filter((product) =>
        productMatchesTicketLicenseType(
          product.Product_LicenseType,
          ticketLicenseType,
        ),
      ),
    [products, ticketLicenseType],
  );

  useEffect(() => {
    // Step-aware validation logic
    if (activeStep === 0) {
      // For step 0 (upload), continue is enabled after upload completion
      setCanContinue(uploadCompleted);
    } else if (activeStep === 1) {
      // For step 1 (details), continue is enabled when all required fields are filled
      setCanContinue(ticketInfoValid());
    } else {
      // For step 2 (coverage), continue is enabled when coverage is selected
      setCanContinue(coverage.length > 0);
    }
  }, [
    activeStep,
    uploadCompleted,
    ticketNumber,
    ticketAddress,
    ticketCitation,
    ticketCity,
    ticketCounty,
    ticketCourtDate,
    ticketRespondByDate,
    ticketLicenseType,
    ticketZone,
    ticketGender,
    ticketPronounSet,
    selectedCourt,
    coverage,
  ]);

  useEffect(() => {
    if (activeStep === 1 && uploadedViaImage && !ocrVerifyAcknowledged) {
      setOcrVerifyModalOpen(true);
    }
  }, [activeStep, uploadedViaImage, ocrVerifyAcknowledged]);

  const handleTicketUploaded = (
    documentId: string,
    parsedData?: ParsedTicketData,
    meta?: { ocrSucceeded: boolean },
  ) => {
    console.log('Ticket uploaded:', documentId, parsedData);

    if (parsedData) {
      applyParsedTicketData(parsedData, {
        setTicketNumber,
        setTicketCitation,
        setTicketAddress,
        setTicketCity,
        setTicketCounty,
        setTicketCourtDate,
        setTicketLicenseType,
        // Respond-by is required on this form and was typed by hand on every upload until now.
        setTicketRespondByDate,
      });
    }

    setUploadedDocumentGuid(documentId.trim() || null);
    setUploadedViaImage(true);
    setOcrParseSucceeded(meta?.ocrSucceeded ?? true);
    setOcrVerifyAcknowledged(false);
    setUploadCompleted(true);
    setActiveStep(1);
  };

  const ticketInfoValid = () => {
    // Streamlined manual entry (G.10, per Lloyd Jul 6): the only fields required to create a
    // case are the client (already chosen via User_guid), the court, and the respond-by date.
    // Everything else can be filled in later. Gender/pronoun is still guarded for consistency.
    return (
      isCourtSelected(selectedCourt?.Court_guid) &&
      ticketRespondByDate.trim().length > 0 &&
      isGenderPronounValid(ticketGender, ticketPronounSet)
    );
  };

  const createTicketReq = async () => {
    if (submitting) {
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(
        (process.env.API_HOST || '') + '/api/v1/ticket',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            Ticket_Number: ticketNumber,
            Ticket_Representation: coverage,
            Ticket_Citation: ticketCitation,
            Ticket_Address: ticketAddress,
            Ticket_City: ticketCity,
            Ticket_County: ticketCounty,
            Ticket_CourtDate: ticketCourtDate,
            Ticket_RespondByDate: ticketRespondByDate,
            Ticket_LicenseType: ticketLicenseType,
            Ticket_Zone: ticketZone,
            Ticket_Url: ticketUrl,
            User_guid: userGuid,
            Prosecutor_guid: selectedProsecutor?.Prosecutor_guid,
            Court_guid: selectedCourt?.Court_guid,
            Ticket_DriversLicenseNumber:
              driversLicenseNumber.trim() || undefined,
            Ticket_Gender: ticketGender || undefined,
            Ticket_PronounSet: ticketPronounSet || undefined,
            Ticket_HasTakenDscInPastYear: hasTakenDscInPastYear,
            ...(uploadedDocumentGuid
              ? { UserDocument_guid: uploadedDocumentGuid }
              : {}),
          }),
        },
      );

      if (!response.ok) {
        // Carry the server's reason through. This used to throw "Network response was not ok" and
        // show a flat "could not create ticket at this time", which is what staff actually saw when
        // the real answer was a named missing field — Maria Elena, Aug 12, spent an hour on a case
        // the server was declining for a specific, fixable reason it had already put in the body.
        const failure = await response.json().catch(() => ({}) as {
          error?: string;
          message?: string;
        });
        throw new Error(
          failure.error ||
            failure.message ||
            'Could not create the case. Please try again.',
        );
      }

      const jsonData = await response.json();
      navigate('/ticket?Ticket_guid=' + jsonData.ticket.Ticket_guid);
    } catch (error) {
      console.error('Error creating ticket:', error);
      alert(
        error instanceof Error
          ? error.message
          : 'Could not create the case. Please try again.',
      );
      setSubmitting(false);
    }
  };

  return (
    <>
      <TicketOcrVerifyDialog
        open={ocrVerifyModalOpen}
        onClose={() => {
          setOcrVerifyModalOpen(false);
          setOcrVerifyAcknowledged(true);
        }}
        ocrSucceeded={ocrParseSucceeded}
      />
      <div className="ntickv">
        <Container className="ntickv-ctn">
          <Typography variant="h1" className="ntickv-ctn-ttl">
            New Ticket
          </Typography>
          <div className="ntickv-ctn-stepper">
            <Stepper activeStep={activeStep}>
              {CREATE_TICKET_STEP_LABELS.map((label, step) => (
                <Step key={step}>
                  <StepLabel className="ntickv-ctn-stepper-lbl">
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </div>
          <br />
          {activeStep === 0 && (
            <>
              <Typography>
                First, upload a photo or PDF of your ticket.
              </Typography>
              <UploadTicket
                uploaded={handleTicketUploaded}
                User_guid={userGuid || undefined}
              />
              <br />
              <Typography
                variant="body2"
                style={{ marginTop: '16px', textAlign: 'center' }}
              >
                or{' '}
                <Button
                  variant="text"
                  onClick={() => {
                    setUploadedViaImage(false);
                    setUploadedDocumentGuid(null);
                    setUploadCompleted(true);
                  }}
                  style={{ textTransform: 'none' }}
                >
                  skip upload and enter details manually
                </Button>
              </Typography>
            </>
          )}
          {activeStep === 1 && (
            <>
              <Typography>
                Enter the case essentials. Everything else is optional and can be
                added later.
              </Typography>
              {/* Essentials — the minimum required to create a case */}
              <div className="ntickv-ctn-form">
                <CourtSelect
                  value={selectedCourt}
                  onChange={(court) => {
                    setSelectedCourt(court);
                    // Reset prosecutor when court changes
                    if (
                      selectedProsecutor &&
                      selectedProsecutor.court?.Court_guid !== court?.Court_guid
                    ) {
                      setSelectedProsecutor(null);
                    }
                  }}
                  label="Court"
                  placeholder="Search and select a court..."
                  required
                />
                <TextField
                  label="Respond-by date (required)"
                  type="date"
                  value={ticketRespondByDate}
                  onChange={(e) => setTicketRespondByDate(e.target.value)}
                  required
                  error={ticketRespondByDate.trim().length === 0}
                  helperText={
                    ticketRespondByDate.trim().length === 0
                      ? 'The court respond-by date is required.'
                      : undefined
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </div>

              {/* More details — optional, collapsed by default for fast entry */}
              <Accordion variant="outlined" sx={{ mt: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>More details (optional)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <div className="ntickv-ctn-form">
                    <TextField
                      label="Citation # (Optional)"
                      value={ticketNumber}
                      onChange={(e) => setTicketNumber(e.target.value)}
                      helperText="Court citation / reference number — can be added before approval."
                    />
                    <TextField
                      label="Offense description (Optional)"
                      value={ticketCitation}
                      onChange={(e) => setTicketCitation(e.target.value)}
                    />
                    <TextField
                      label="Violation Address (Optional)"
                      value={ticketAddress}
                      onChange={(e) => setTicketAddress(e.target.value)}
                    />
                    <TextField
                      label="Violation City (Optional)"
                      value={ticketCity}
                      onChange={(e) => setTicketCity(e.target.value)}
                    />
                    <TextField
                      label="Violation County (Optional)"
                      value={ticketCounty}
                      onChange={(e) => setTicketCounty(e.target.value)}
                    />
                    <TextField
                      label="Court Date (Optional)"
                      type="date"
                      value={ticketCourtDate}
                      onChange={(e) => setTicketCourtDate(e.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                    <TextField
                      label="Driver's License # (Optional)"
                      value={driversLicenseNumber}
                      onChange={(e) => setDriversLicenseNumber(e.target.value)}
                    />
                    <GenderPronounFields
                      gender={ticketGender}
                      pronounSet={ticketPronounSet}
                      onGenderChange={(gender, pronounSet) => {
                        setTicketGender(gender);
                        setTicketPronounSet(pronounSet);
                      }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={hasTakenDscInPastYear}
                          onChange={(e) =>
                            setHasTakenDscInPastYear(e.target.checked)
                          }
                        />
                      }
                      label="Has taken a driver safety course in the past year"
                    />
                    <FormControl fullWidth>
                      <InputLabel id="ticket-license-type-label">
                        License Type (Optional)
                      </InputLabel>
                      <Select
                        value={ticketLicenseType}
                        label="License Type (Optional)"
                        onChange={(e) => setTicketLicenseType(e.target.value)}
                      >
                        <MenuItem value="">Not Specified</MenuItem>
                        <MenuItem value="Class C">Class C</MenuItem>
                        <MenuItem value="CDL">CDL</MenuItem>
                        <MenuItem value="None">No License</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth>
                      <InputLabel id="ticket-zone-label">
                        License Zone (Optional)
                      </InputLabel>
                      <Select
                        value={ticketZone}
                        label="License Zone (Optional)"
                        onChange={(e) => setTicketZone(e.target.value)}
                      >
                        <MenuItem value="">None</MenuItem>
                        <MenuItem value="Street">Street</MenuItem>
                        <MenuItem value="Highway">Highway</MenuItem>
                        <MenuItem value="Interstate">Interstate</MenuItem>
                        <MenuItem value="Parking">Parking</MenuItem>
                        <MenuItem value="School Zone">School Zone</MenuItem>
                        <MenuItem value="Construction Zone">
                          Construction Zone
                        </MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                      </Select>
                    </FormControl>
                    <ProsecutorSelect
                      value={selectedProsecutor}
                      onChange={setSelectedProsecutor}
                      label="Prosecutor (Optional)"
                      placeholder="Search and select a prosecutor..."
                      disabled={!selectedCourt}
                      courtGuidFilter={selectedCourt?.Court_guid}
                      helperText={
                        !selectedCourt
                          ? 'Please select a court first'
                          : undefined
                      }
                    />
                  </div>
                </AccordionDetails>
              </Accordion>
            </>
          )}
          {activeStep === 2 && (
            <>
              {ticketLicenseType &&
                ticketLicenseTypeToCode(ticketLicenseType) && (
                  <Box sx={{ mb: 2 }}>
                    <Chip
                      label={`Plans for: ${ticketLicenseType}`}
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                )}
              <Typography>Select your level of care</Typography>
              {!loadingProducts &&
                ticketLicenseType &&
                matchingProducts.length === 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    No plans are available for {ticketLicenseType}.
                  </Alert>
                )}
              <Select
                value={coverage}
                label="Coverage"
                onChange={(e) => {
                  setCoverage(e.target.value);
                  setCanContinue(true);
                }}
                className="ntickv-ctn-coverage-select"
                disabled={loadingProducts || matchingProducts.length === 0}
              >
                {loadingProducts ? (
                  <MenuItem disabled>Loading pricing options...</MenuItem>
                ) : matchingProducts.length === 0 ? (
                  <MenuItem disabled>No matching plans available</MenuItem>
                ) : (
                  matchingProducts.map((product) => (
                    <MenuItem
                      key={product.Product_guid}
                      value={product.Product_Name}
                    >
                      {product.Product_Name} — $
                      {(product.Product_Cost / 100).toFixed(2)}
                    </MenuItem>
                  ))
                )}
              </Select>
              <p>We will reach out later by email to collect payment.</p>
            </>
          )}
          <div className="ntickv-ctn-actns">
            <Button
              disabled={activeStep == 0 || submitting}
              variant="contained"
              color="inherit"
              onClick={() => {
                const newStep = activeStep - 1;
                setActiveStep(newStep);
                // Reset upload completion if going back to step 0
                if (newStep === 0) {
                  setUploadCompleted(false);
                  setUploadedViaImage(false);
                  setUploadedDocumentGuid(null);
                }
              }}
            >
              Back
            </Button>
            <Button
              disabled={!canContinue || submitting}
              variant="contained"
              color="primary"
              onClick={() => {
                if (activeStep === 2) {
                  void createTicketReq();
                } else {
                  setActiveStep(activeStep + 1);
                }
              }}
            >
              {activeStep === 2
                ? submitting
                  ? 'Creating ticket...'
                  : 'Submit Ticket'
                : 'Continue'}
            </Button>
          </div>
        </Container>
      </div>
    </>
  );
};
export default ManagerCreateTicketView;
